import { Box, FormControl, SxProps } from '@mui/material';
import i18next from 'i18next';
import {
  LocalizationProvider,
  DateTimePicker as DTP,
  DateTimePickerProps as DTPProps,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTimeValidationError } from '@mui/x-date-pickers/models';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationErrorIcon from '../Icons/NotificationErrorIcon';
import { FeedbackWrapper, InputLabelWrapper } from '../TextInput/TextInputStyle';
import ButtonField from './ButtonField';
import ActionBar from './ActionBar';
import CalendarHeader from './CalendarHeader';
import DateTimeStyles from './DateTimeStyle';

export interface DateTimeCalendarProps extends DTPProps<DateTime> {
  error?: boolean;
  feedback?: JSX.Element | string;
  formStyle?: SxProps;
  triggerClear?: boolean;
  placeholder?: string;
  minDate?: DateTime;
  maxDate?: DateTime;
  optional?: boolean;
  onBlur?: () => void;
}

function DateTimeCalendar(props: DateTimeCalendarProps) {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const { language } = i18next;
  const [componentKey, setComponentKey] = useState(Date.now());
  const [tempValue, setTempValue] = useState<DateTime | null>(props.value ?? null);

  useEffect(() => {
    // forces the reset of the component when a reset filter is applied
    setComponentKey(Date.now());
    setTempValue(props.value ?? null);
  }, [props.triggerClear, props.value]);

  /**
   * Handler for cancel button - closes picker without saving changes
   */
  const handleCancel = () => {
    setTempValue(props.value ?? null);
    setOpen(false);
    props.onBlur?.();
  };

  /**
   * Handler for save button - saves the selected date/time and closes picker
   */
  const handleSave = () => {
    if (props.onChange && tempValue) {
      const context: PickerChangeHandlerContext<DateTimeValidationError> = {
        validationError: null,
      };
      props.onChange(tempValue, context);
    }
    setOpen(false);
    props.onBlur?.();
  };

  /**
   * Check if the selected value is valid
   */
  const isValueValid = () => {
    if (!tempValue) return false;

    const now = DateTime.now();
    if (
      tempValue < now ||
      (props.minDate && tempValue < props.minDate) ||
      (props.maxDate && tempValue > props.maxDate)
    ) {
      return false;
    }

    return true;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={language}>
      <FormControl sx={{ width: '100%', ...props.formStyle }}>
        {props.label ? (
          <InputLabelWrapper shrink sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            {props.label}
            {props.optional ? <FeedbackWrapper component="span">{t('common.optional')}</FeedbackWrapper> : null}
          </InputLabelWrapper>
        ) : null}
        <DTP
          key={componentKey}
          onError={props.onError}
          onChange={(newValue) => setTempValue(newValue)}
          value={tempValue}
          open={open}
          onClose={handleCancel}
          disablePast
          ampm={false}
          closeOnSelect={false}
          slots={{
            field: ButtonField,
            calendarHeader: CalendarHeader,
            actionBar: ActionBar,
          }}
          minDate={props.minDate}
          maxDate={props.maxDate}
          slotProps={{
            field: {
              placeholder: props.placeholder ?? 'dd / mm / yyyy , --:--',
              error: props.error,
              onClick: () => setOpen((prev) => !prev),
            } as any,
            layout: {
              sx: DateTimeStyles,
            },
            actionBar: {
              onAccept: handleSave,
              onCancel: handleCancel,
              saveDisabled: !isValueValid(),
              actions: [],
            } as any,
          }}
          views={['day', 'hours', 'minutes']}
          format="dd/MM/yyyy HH:mm"
        />
        {props.feedback ? (
          <FeedbackWrapper error={props.error} component="span">
            {props.error ? (
              <Box sx={{ paddingRight: '0.25rem', flexShrink: 0 }}>
                <NotificationErrorIcon />
              </Box>
            ) : null}
            {props.feedback}
          </FeedbackWrapper>
        ) : null}
      </FormControl>
    </LocalizationProvider>
  );
}

export default DateTimeCalendar;
